import React, { FC, memo } from "react";
import "./CustomButtons.scss";

/**
 * Button variants type
 */
type ButtonVariant = "primary" | "secondary" | "outline" | "text";

/**
 * Button sizes type
 */
type ButtonSize = "small" | "medium" | "large";

/**
 * CustomButton Props Interface
 * @interface CustomButtonProps
 */
interface CustomButtonProps {
  /** HTML button type */
  type?: "button" | "submit" | "reset";
  /** Button variant style */
  variant: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Click handler */
  onClick?: () => void;
  /** Button content */
  children: React.ReactNode;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Additional button class names */
  className?: string;
  /** Additional content wrapper class names */
  contentClass?: string;
  /** Whether button is in loading state */
  isLoading?: boolean;
  /** Loading spinner color */
  spinnerColor?: string;
  /** Additional HTML button attributes */
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * CustomButton Component
 * A reusable button component with support for different variants, sizes,
 * loading states, and accessibility features.
 */
const CustomButton: FC<CustomButtonProps> = ({
  type = "button",
  variant,
  size = "medium",
  onClick,
  children,
  disabled = false,
  className = "",
  contentClass = "d-flex",
  isLoading = false,
  spinnerColor = "#ffffff",
  buttonProps = {},
  ref,
}) => {
  const buttonClassName = `
    button
    button--${variant}
    button--${size}
    ${isLoading ? "button--loading" : ""}
    ${className}
  `.trim();

  return (
    <button
      type={type}
      ref={ref}
      className={buttonClassName}
      onClick={onClick}
      disabled={disabled || isLoading}
      aria-disabled={disabled || isLoading}
      aria-busy={isLoading}
      data-testid={"submit-btn"}
      {...buttonProps}
    >
      <div className={`button__content ${contentClass}`.trim()}>
        {isLoading && (
          <div
            className="spinner-border spinner-border-sm text-light me-2 p-"
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        )}
        <span className={isLoading ? "button__children--loading hide-svg-icon" : ""}>
          {children}
        </span>
      </div>
    </button>
  );
};

export default memo(CustomButton);
